import React, { useState, useEffect } from 'react';
import Snackbar from './Snackbar';
import { getApiBaseUrl } from '../config/api';
import './UserManagement.css';

const UserManagement = ({ user, onShowNotification }) => {
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('info');
  const [editingAgent, setEditingAgent] = useState(null);
  const [editForm, setEditForm] = useState({ username: '', email: '' });
  const [editError, setEditError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const API_BASE_URL = getApiBaseUrl();

  const showNotification = (message, type = 'info') => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setShowSnackbar(true);
    if (onShowNotification) {
      onShowNotification(message, type);
    }
  };

  const getAuthToken = () => {
    return localStorage.getItem('adminToken');
  };

  const fetchAgents = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await fetch(`${API_BASE_URL}/api/admin/agents?${params}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setAgents(data.agents || data.data || []);
        setPagination(data.pagination || { page: currentPage, limit: itemsPerPage, total: data.agents?.length || 0, totalPages: 1 });
      } else {
        showNotification(data.error || 'Failed to fetch agents', 'error');
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      showNotification('Failed to fetch agents', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (['super_admin', 'admin', 'credit_control'].includes(user?.role)) {
      fetchAgents();
    }
  }, [user, currentPage, searchTerm]);

  const openEditLogin = (agent) => {
    setEditingAgent(agent);
    setEditForm({ username: agent.username || '', email: agent.email || '' });
    setEditError('');
  };

  const closeEditLogin = () => {
    if (!isSaving) {
      setEditingAgent(null);
      setEditForm({ username: '', email: '' });
      setEditError('');
    }
  };

  const handleSaveLoginDetails = async () => {
    if (!editingAgent) return;
    const username = (editForm.username || '').trim();
    const email = (editForm.email || '').trim();
    if (!username && !email) {
      setEditError('Enter at least a username or email.');
      return;
    }
    if (username && username.length < 2) {
      setEditError('Username must be at least 2 characters.');
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEditError('Enter a valid email address.');
      return;
    }
    setEditError('');
    setIsSaving(true);
    try {
      const body = {};
      if (username) body.username = username;
      if (email !== undefined) body.email = email;
      const response = await fetch(`${API_BASE_URL}/api/admin/agents/${editingAgent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify(body)
      });
      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        setEditError(response.ok ? 'Unexpected server response.' : `Update failed (${response.status}). Please try again.`);
        return;
      }
      if (data.success) {
        showNotification('Login details updated successfully', 'success');
        setEditingAgent(null);
        setEditForm({ username: '', email: '' });
        setEditError('');
        fetchAgents();
      } else {
        setEditError(data.error || 'Failed to update');
      }
    } catch (err) {
      console.error('Error updating agent login:', err);
      setEditError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleAgentStatus = async (agentId, currentStatus) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} this agent?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/agents/${agentId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ is_active: newStatus })
      });

      const data = await response.json();
      if (data.success) {
        showNotification(`Agent ${action}d successfully`, 'success');
        fetchAgents();
      } else {
        showNotification(data.error || `Failed to ${action} agent`, 'error');
      }
    } catch (error) {
      console.error(`Error ${action}ing agent:`, error);
      showNotification(`Failed to ${action} agent`, 'error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!['super_admin', 'admin', 'credit_control'].includes(user?.role)) {
    return (
      <div className="user-management">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access agent management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <div className="header-content">
          <h2>👥 Agent Management</h2>
          <p>Manage agents and their account status</p>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="agent-filter-bar">
        <div className="filter-group">
          <label className="filter-label">
            <span className="filter-icon">🔍</span>
            Search Agents:
          </label>
          <input
            type="text"
            placeholder="Search by agency name, IRA, username, email..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
        </div>
        {searchTerm && (
          <button
            className="clear-filters-btn"
            onClick={() => {
              setSearchTerm('');
              setCurrentPage(1);
            }}
          >
            ✕ Clear Search
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading agents...</p>
        </div>
      ) : (
        <div className="agents-table-container">
          <table className="agents-table">
            <thead>
              <tr>
                <th>Agency Name</th>
                <th>IRA Number</th>
                <th>Username</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Verified</th>
                <th>Revenue</th>
                <th>Transactions</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 ? (
                <tr>
                  <td colSpan="11" className="no-data">
                    No agents found. {searchTerm ? 'Try adjusting your search.' : ''}
                  </td>
                </tr>
              ) : (
                agents.map((agent) => (
                  <tr key={agent.id} className={!agent.is_active ? 'inactive-agent' : ''}>
                    <td>
                      <div className="agency-info">
                        <strong>{agent.agency_name || 'N/A'}</strong>
                        {(agent.tq_code || agent.tqCode) && (
                          <span className="agency-desc">{agent.tq_code || agent.tqCode}</span>
                        )}
                      </div>
                    </td>
                    <td>{agent.ira_number || 'N/A'}</td>
                    <td>{agent.username || 'N/A'}</td>
                    <td>{agent.email || 'N/A'}</td>
                    <td>{agent.phone || 'N/A'}</td>
                    <td>
                      <span className={`status-badge ${agent.is_active ? 'active' : 'inactive'}`}>
                        {agent.is_active ? '✅ Active' : '❌ Inactive'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${agent.isverified ? 'verified' : 'unverified'}`}>
                        {agent.isverified ? '✓ Verified' : '⚠ Unverified'}
                      </span>
                    </td>
                    <td className="revenue-cell">
                      {formatCurrency(agent.total_revenue || 0)}
                    </td>
                    <td>{agent.total_transactions || 0}</td>
                    <td>{formatDate(agent.created_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          type="button"
                          className="edit-login-btn"
                          onClick={() => openEditLogin(agent)}
                          title="Edit username and email"
                        >
                          ✏️ Edit login
                        </button>
                        <button
                          className={`toggle-status-btn ${agent.is_active ? 'deactivate' : 'activate'}`}
                          onClick={() => handleToggleAgentStatus(agent.id, agent.is_active)}
                          title={agent.is_active ? 'Deactivate agent' : 'Activate agent'}
                        >
                          {agent.is_active ? '🚫 Deactivate' : '✅ Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {pagination.totalPages} ({pagination.total} total agents)
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                disabled={currentPage === pagination.totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Edit Login Modal */}
      {editingAgent && (
        <div className="modal-overlay" onClick={closeEditLogin}>
          <div className="modal-content edit-login-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit login — {editingAgent.agency_name || editingAgent.ira_number}</h3>
              <button type="button" className="close-btn" onClick={closeEditLogin} disabled={isSaving} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              {editError && <div className="edit-login-error">{editError}</div>}
              <div className="form-group">
                <label htmlFor="edit-username">Username</label>
                <input
                  id="edit-username"
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm((f) => ({ ...f, username: e.target.value }))}
                  placeholder="Agent login username"
                  disabled={isSaving}
                  autoComplete="username"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-email">Email</label>
                <input
                  id="edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="agent@example.com"
                  disabled={isSaving}
                  autoComplete="email"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={closeEditLogin} disabled={isSaving}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={handleSaveLoginDetails} disabled={isSaving}>
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
          duration={5000}
        />
      )}
    </div>
  );
};

export default UserManagement;
